import React, { useContext, useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import { brandColours } from '../styles';
import Layout from '../components/Layout';
import Helmet from '../components/Helmet';
import Sailthru from '../components/Sailthru';
import PageSectionLinks from '../components/PageSectionLinks';
import SpaIntro from '../components/SpaIntro';
import SpaPackages from '../components/SpaPackages';
import RecentlyViewed from '../components/RecentlyViewed';
import InfoTable from '../components/InfoTable';
import FeaturedIconPoints from '../components/FeaturedIconPoints';
import Accordion from '../components/Accordion';
import SpaLocationGroup from '../components/SpaLocationGroup';
import SpaDirections from '../components/SpaDirections';
import SpaReviews from '../components/SpaReviews';
import { StoreContext } from '../context/StoreContext';

const SpaTemplate = ({
  data: {
    spaseekers: {
      spa: {
        spaId,
        seo,
        dataLayer,
        breadcrumbs,
        sailthru,
        name,
        nameWithLocation,
        tags,
        isLuxury,
        spaGroup,
        address,
        county,
        region,
        directions,
        spaRating,
        images,
        importantAdvice,
        shortDescription,
        content: {
          introText,
          relaxationText,
          fitnessText,
          accomodationText,
          cuisineText,
        },
        facilities,
        minPrice,
        higherPrice,
        maxPrice,
        isPricePerPerson,
        packages,
        attributes,
        location,
        reviews,
        tripAdvisor,
        tripAdvisorReviews,
        giftVouchers,
        frequentlyAskedQuestions,
      },
      savedSpa,
    },
  },
  location: { state },
}) => {
  const {
    addSpaToRecentlyViewed,
    setOverlayActive,
    triggerCjAffiliatePageView,
  } = useContext(StoreContext);

  const defaultActiveSpaPackagesFilters = {
    all: true,
    'spa-deals': false,
    'spa-days': false,
    'spa-breaks': false,
    'spa-vouchers': false,
  };

  const [activeSpaPackagesFilters, setActiveSpaPackagesFilters] = useState(
    defaultActiveSpaPackagesFilters,
  );

  const [isSpaEnquiryOpen, setSpaEnquiryOpen] = useState();

  useEffect(() => {
    addSpaToRecentlyViewed(savedSpa);
  }, []);

  useEffect(() => {
    if (state && state.fromFeaturedSpaCard) {
      const element = document.getElementById('spa-packages');

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 1000);
      }
    }
  }, [state]);

  const toggleSpaEnquiryOpen = () => {
    setOverlayActive(!isSpaEnquiryOpen);
    setSpaEnquiryOpen(!isSpaEnquiryOpen);
  };

  const altTheme = isLuxury ? true : false;

  const popularFacilities =
    facilities && facilities.filter(({ popular }) => popular);

  const facilityGroupNames = facilities && [
    ...new Set(facilities.map(({ groups }) => groups).flat()),
  ];

  const facilityGroups =
    facilityGroupNames &&
    facilityGroupNames.map(facilityGroupName => ({
      name: facilityGroupName === null ? 'Facilities' : facilityGroupName,
      facilities: facilities.map(facility =>
        facility.groups === null
          ? facility
          : facility.groups.includes(facilityGroupName)
            ? facility
            : '',
      ),
    }));

  useEffect(() => {
    triggerCjAffiliatePageView('productDetail');
  }, []);

  return (
    <Layout
      dataLayer={dataLayer}
      headerBackgroundColour={
        altTheme ? brandColours.denary : brandColours.senary
      }
      altTheme={altTheme}
    >
      <Helmet seo={seo} ogImage={images && images[0]}>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@id": "${process.env.GATSBY_SITE_URL}${
              seo.canonicalUrl ? seo.canonicalUrl : seo.url
            }",
            "name": "${name}",
            "url": "${process.env.GATSBY_SITE_URL}${
              seo.canonicalUrl ? seo.canonicalUrl : seo.url
            }",
            "description": "${shortDescription}",
            "image": "${images && images[0].url}",
            "priceRange": "${minPrice} - ${maxPrice}",
            "potentialAction": "buyAction",
            "address": {
              "addressLocality": "${county.name}",
              "addressRegion": "${region.name}",
              "postalCode": "${address.postcode}",
              "streetAddress": "${address.line1}",
              "telephone": "${address.telephone}",
              "@type": "PostalAddress"
            },
            "amenityFeature": [
              ${
                popularFacilities &&
                popularFacilities.map(({ text }) =>
                  JSON.stringify({
                    name: text,
                    '@type': 'LocationFeatureSpecification',
                    value: 'true',
                  }),
                )
              }
            ],
            "hasOfferCatalog": {
              "name": "packages",
              "itemListElement": [
                ${packages.map(({ minPrice, canonicalUrl, name }, i) =>
                  JSON.stringify({
                    item: {
                      '@context': 'https://schema.org',
                      availability: 'https://schema.org/InStock',
                      priceCurrency: 'GBP',
                      price: minPrice,
                      '@type': 'Offer',
                      url: process.env.GATSBY_SITE_URL + canonicalUrl,
                    },
                    name: name,
                    position: i,
                    '@type': 'ListItem',
                  }),
                )}
              ],
              "@type": "OfferCatalog"
            },
            ${
              reviews.length > 0
                ? `
            "aggregateRating": {
              "ratingCount": "${reviews.length}",
              "bestRating": "5",
              ${spaRating && `"ratingValue": "${spaRating.rating}"`}
            },
            "review": [
              ${reviews.map(
                ({ firstName, lastName, title, text, overallRating }) =>
                  JSON.stringify({
                    '@type': 'Review',
                    author: {
                      '@type': 'Person',
                      name: `${firstName} ${lastName}`,
                    },
                    reviewRating: {
                      '@type': 'Rating',
                      ratingValue: !overallRating ? 1 : overallRating,
                    },
                    reviewBody: text,
                    name: title,
                  }),
              )}
            ],
            `
                : ''
            }
            "@type": "DaySpa"
          }
        `}</script>
      </Helmet>
      <Sailthru data={sailthru} />
      <main>
        <article>
          <PageSectionLinks
            items={[
              {
                id: 'spa-gallery',
                text: 'Gallery',
              },
              {
                id: 'spa-introduction',
                text: 'Introduction',
              },
              {
                id: 'spa-about',
                text: 'About',
              },
              {
                id: 'spa-packages',
                text: 'Packages',
              },
              {
                id: 'spa-details',
                text: 'Details',
              },
              {
                id: 'spa-map-location',
                text: 'Map Location',
              },
              {
                id: 'spa-reviews',
                text: 'Reviews',
              },
            ]}
            trigger="spa-gallery"
            altTheme={altTheme}
          />
          <SpaIntro
            heading={seo.h1 || name}
            breadcrumbs={breadcrumbs}
            address={
              address.telephone
                ? Object.values(address).slice(0, -1).filter(Boolean).join(', ')
                : Object.values(address).filter(Boolean).join(', ')
            }
            minPrice={minPrice}
            higherPrice={higherPrice}
            isPricePerPerson={isPricePerPerson}
            rating={spaRating && spaRating.rating}
            images={images}
            isLuxury={isLuxury}
            isJenniferYoung={tags.includes('JenniferYoung')}
            importantAdvice={importantAdvice}
            content={introText}
            popularFacilities={popularFacilities}
            shareData={{
              title: seo.title,
              text: seo.metaDescription,
              url:
                process.env.GATSBY_SITE_URL + seo.canonicalUrl
                  ? seo.canonicalUrl
                  : seo.url,
            }}
            wishlistSpa={savedSpa}
            altTheme={altTheme}
            hasPackages={packages && packages.length}
            giftVouchers={giftVouchers}
            activateSpaVouchersFilter={() =>
              setActiveSpaPackagesFilters({
                ...activeSpaPackagesFilters,
                all: false,
                'spa-vouchers': true,
              })
            }
          />
          <SpaPackages
            items={packages}
            spa={{
              id: spaId,
              name: name,
              group: spaGroup && spaGroup.name,
              county: county.name,
              images: images,
              nameWithLocation: nameWithLocation,
            }}
            toggleSpaEnquiryOpen={toggleSpaEnquiryOpen}
            altTheme={altTheme}
            giftVouchers={giftVouchers}
            minAge={
              attributes.find(({ name }) => name === 'Minimum age') &&
              attributes.find(({ name }) => name === 'Minimum age').value
            }
            defaultActiveFilters={defaultActiveSpaPackagesFilters}
            activeFilters={activeSpaPackagesFilters}
            setActiveFilters={setActiveSpaPackagesFilters}
            isSpaEnquiryOpen={isSpaEnquiryOpen}
          />
          <RecentlyViewed currentSpaId={spaId} />
          <div id="spa-details">
            <InfoTable
              heading="Details"
              rows={attributes}
              altTheme={altTheme}
            />
          </div>
          {facilityGroups &&
            facilityGroups.map(({ name, facilities }, id) => (
              <FeaturedIconPoints
                key={id}
                heading={name}
                items={facilities}
                altTheme={altTheme}
              />
            ))}
          <Accordion
            items={[
              {
                heading: `The Spa & Wellness at ${name}`,
                content: relaxationText,
              },
              {
                heading: `The Gym & Fitness at ${name}`,
                content: fitnessText,
              },
              {
                heading: 'The Rooms',
                content: accomodationText,
              },
              {
                heading: `Restaurants & Food at ${name}`,
                content: cuisineText,
              },
            ]}
            altTheme={altTheme}
          />
          <div id="spa-map-location">
            <SpaLocationGroup
              spa={{
                name,
                nameWithLocation,
                address,
                location,
              }}
              spaGroup={spaGroup}
            />
          </div>
          <SpaDirections directions={directions} />
          <SpaReviews
            spaSeekers={{
              summary: spaRating && {
                rating: spaRating.rating,
                scores: [
                  {
                    name: 'Staff service & quality',
                    score: spaRating.averageServiceRating,
                  },
                  {
                    name: 'Condition & cleanliness',
                    score: spaRating.averageConditionRating,
                  },
                  {
                    name: 'Overall satisfaction',
                    score: spaRating.averageOverallRating,
                  },
                ],
              },
              items: reviews,
              reviewLink: `/review-a-spa/${seo.slug}`,
            }}
            {...(tripAdvisorReviews && {
              tripAdvisor: {
                id: tripAdvisor && tripAdvisor.tripAdvisorId,
                summary: {
                  overallRating: tripAdvisorReviews.overallRating,
                  ratingImageUrl: tripAdvisorReviews.ratingImageUrl,
                  numberOfReviews: tripAdvisorReviews.numberOfReviews,
                  dimensions: Object.entries(tripAdvisorReviews.dimensions),
                  ratings: Object.entries(tripAdvisorReviews.ratings),
                },
                items: tripAdvisorReviews.reviews,
              },
            })}
            altTheme={altTheme}
          />
          {frequentlyAskedQuestions && (
            <Accordion
              heading={frequentlyAskedQuestions.title}
              items={frequentlyAskedQuestions.questions}
              isFaqs={true}
              altTheme={altTheme}
            />
          )}
        </article>
      </main>
    </Layout>
  );
};

export const SpaTemplateQuery = graphql`
  query SpaTemplateQuery($id: Int!) {
    spaseekers {
      spa(spaID: $id) {
        spaId
        seo {
          ...HelmetFragment
          slug
          h1
        }
        dataLayer {
          ...DataLayerFragment
        }
        breadcrumbs {
          ...BreadcrumbsFragment
        }
        sailthru {
          ...SailthruFragment
        }
        name
        nameWithLocation
        tags
        isLuxury
        spaGroup {
          name
          url
          images {
            ...ImageFragment
            role
          }
        }
        address {
          line1
          line2
          line3
          city
          postcode
          telephone
        }
        county {
          name
        }
        region {
          name
        }
        directions
        spaRating {
          rating
          averageServiceRating
          averageConditionRating
          averageOverallRating
        }
        images {
          url
          title
          width
          height
          role
        }
        importantAdvice {
          enquiryButtonEnabled
          heading
          subHeading
          adviceHtml
        }
        shortDescription
        content {
          introText
          relaxationText
          fitnessText
          accomodationText
          cuisineText
        }
        facilities {
          text: facility
          icon
          popular
          groups
        }
        minPrice
        higherPrice
        maxPrice
        isPricePerPerson
        packages {
          spaId
          packageId
          canonicalUrl
          name
          features
          url
          deal {
            text
            amount
          }
          liveAvailability
          checkAvailability
          availableForBooking
          earliestDate
          minPrice
          higherPrice
          isPricePerPerson
          guestsPerPackage
          maxGuestsPerPackage
          maxOccupancy
          voucherOptions: options {
            bookingOptionId
            price
            fullName
          }
          minQuantity
          flags {
            isBooking
            isDeal
            isDay
            isBreak
            isVoucher
          }
          yielding
        }
        attributes {
          name
          value
        }
        location {
          latitude
          longitude
        }
        reviews {
          ...SpaSeekersReviewFragment
        }
        tripAdvisor {
          tripAdvisorId
        }
        tripAdvisorReviews {
          overallRating
          numberOfReviews
          dimensions {
            location
            sleepQuality
            rooms
            service
            value
            cleanliness
          }
          ratings {
            fiveStar
            fourStar
            threeStar
            twoStar
            oneStar
          }
          reviews {
            title
            rating
            ratingImageUrl
            review
            reviewUrl
          }
        }
        giftVouchers
        frequentlyAskedQuestions {
          title
          questions {
            heading: title
            content
          }
        }
      }
      savedSpa: spa(spaID: $id) {
        spaId
        name
        tags
        images {
          url
          title
        }
        address {
          city
        }
        county {
          name
        }
        location {
          latitude
          longitude
        }
        shortDescription
        packageCount: packages {
          packageId
        }
        facilities {
          facility
        }
        spaRating {
          rating
        }
        minPrice
        isPricePerPerson
        url
        isLuxury
      }
    }
  }
`;

export default SpaTemplate;
